import _ from 'lodash';
import React, { MouseEvent, useCallback, useContext, useEffect, useState } from 'react';

import { Table, TableBody, TableContainer } from '@mui/material';

import { AttendanceContext, View } from '../../context/AttendanceContext';
import { getComparator } from '../../utils/sortingUtils';
import Modal from '../Modal';
import AttendanceEditForm from './AttendanceEditForm';
import AttendanceTableHead from './AttendanceTableHead';
import AttendanceTableRow from './AttendanceTableRow';
import NoAttendanceView from './NoAttendanceView';
import { ClassSessionView, AttendanceTableColumns, Order } from './types';
import { useAttendanceData } from './useAttendanceData';

const AttendanceTable: React.FC = () => {
    const { view, setSelectedStudent, setSelectedSession, setTotals, markAttendance } = useContext(AttendanceContext);
    const { sessionRows, studentRows } = useAttendanceData();

    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<AttendanceTableColumns>(() => (view === View.Session ? 'sortName' : 'date'));
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (studentRows.length < 1) return;

        const filteredStudentRows: ClassSessionView[] = _.filter(studentRows, (object) => object.found) as ClassSessionView[];
        const present = filteredStudentRows.filter((i) => i.attendance).length;
        const tardy = filteredStudentRows.filter((i) => i.tardiness).length;
        const absent = filteredStudentRows.length - present;

        setTotals({ present, absent, tardy });
    }, [setTotals, studentRows]);

    useEffect(() => {
        switch (view) {
            case View.Session: setOrderBy('sortName'); break;
            case View.Student: setOrderBy('date'); break;
            default: break;
        }
    }, [view]);

    const handleRowClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>, selectedStudent, selectedSession) => {
            setSelectedStudent(selectedStudent);
            setSelectedSession(selectedSession);

            setOpen(true);
        },
        [setSelectedSession, setSelectedStudent]
    );

    const handleMenuClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleRequestSort = useCallback((event: React.MouseEvent<unknown>, property: AttendanceTableColumns) => {
        let sortProperty = property;

        if (property === 'name') {
            sortProperty = 'sortName';
        }

        const isAsc = orderBy === sortProperty && order === 'asc';

        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(sortProperty);
    }, [order, orderBy]);

    const renderTableBody = useCallback(() => {
        let sorted;

        if (view === View.Session) {
            sorted = studentRows.slice().sort(getComparator(order, orderBy));
        }

        if (view === View.Student) {
            sorted = sessionRows.slice().sort(getComparator(order, orderBy));
        }

        sorted = _.filter(sorted, (object) => object.found);
        return sorted.map((row, index) => (
            <AttendanceTableRow key={index} row={row} onClick={handleRowClick}/>
        ));
    }, [handleRowClick, order, orderBy, sessionRows, studentRows, view]);

    if (studentRows.length < 1 && sessionRows.length < 1) {
        return <NoAttendanceView />;
    }

    return (
        <TableContainer>
            <Table aria-labelledby="student-attendance-table" size="medium">
                <AttendanceTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                <TableBody>{renderTableBody()}</TableBody>
            </Table>

            <Modal isOpen={open} onClose={handleMenuClose}>
                <AttendanceEditForm onCancel={handleMenuClose} onFinish={markAttendance} />
            </Modal>
        </TableContainer>
    );
};

export default AttendanceTable;
